import moment from 'moment-timezone';

import { MFA_TYPES } from './cognito-util';
import {
  MFA_DEVICE_EXPIRATION_UNITS,
  MFA_DEVICE_EXPIRATION_VALUE,
} from './env';

const VALID_EXPIRATION_UNITS = [
  'months',
  'weeks',
  'days',
  'hours',
  'minutes',
  'seconds',
];

const DEVICE_REMEMBER_CONFIG = (() => {
  const units =
    typeof MFA_DEVICE_EXPIRATION_UNITS !== 'undefined'
      ? MFA_DEVICE_EXPIRATION_UNITS
      : 'days';
  const value =
    typeof MFA_DEVICE_EXPIRATION_VALUE !== 'undefined'
      ? MFA_DEVICE_EXPIRATION_VALUE
      : 30;

  if (!VALID_EXPIRATION_UNITS.includes(units)) {
    console.error(
      `Invalid MFA_DEVICE_EXPIRATION_UNITS: ${units}. Using default: days`,
    );

    return {
      expirationUnits: 'days',
      expirationValue: parseInt(value, 10),
    };
  }

  return {
    expirationUnits: units,
    expirationValue: parseInt(value, 10),
  };
})();

const getDeviceRememberTimeKey = () =>
  'CognitoIdentityServiceProvider.nebula.deviceRememberTime';

const AUTO_CLEAR_CACHE = 'AUTO';

export const markAutoClearCache = () => {
  const deviceRememberTimeKey = getDeviceRememberTimeKey();
  localStorage.setItem(deviceRememberTimeKey, AUTO_CLEAR_CACHE);
};

const clearCachedCognitoIdentityServiceProviderKeys = () => {
  try {
    const prefix = 'CognitoIdentityServiceProvider.';
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    return true;
  } catch (err) {
    console.error('Error clearing cached device keys:', err);
    return false;
  }
};

/**
 * Checks if the device remember time has expired
 * @returns {boolean} True if the device has expired or the time isn't set, false otherwise
 */
export const checkDeviceExpiration = () => {
  try {
    const deviceRememberTimeKey = getDeviceRememberTimeKey();
    const rememberTimeStr = localStorage.getItem(deviceRememberTimeKey);

    if (!rememberTimeStr) {
      return false;
    }

    if (rememberTimeStr === AUTO_CLEAR_CACHE) {
      clearCachedCognitoIdentityServiceProviderKeys();
      return true;
    }

    const rememberTime = parseInt(rememberTimeStr, 10);
    const remembered = moment(rememberTime);
    const now = moment();

    const diff = now.diff(remembered, DEVICE_REMEMBER_CONFIG.expirationUnits);
    const hasExpired = diff > DEVICE_REMEMBER_CONFIG.expirationValue;

    if (hasExpired) {
      clearCachedCognitoIdentityServiceProviderKeys();
      return true;
    }

    return false;
  } catch (err) {
    console.error('Error checking device expiration:', err);
    return false;
  }
};

const setDeviceRememberTime = () => {
  const deviceRememberTimeKey = getDeviceRememberTimeKey();
  const now = moment().valueOf();
  localStorage.setItem(deviceRememberTimeKey, now.toString());
};

/**
 * Sets the device status as remembered and stores the timestamp
 * @param {object} cognitoUser The Cognito user
 * @returns {Promise} A promise that resolves when the device is remembered
 */
export const setDeviceStatusRememberedSafe = cognitoUser =>
  new Promise((resolve, _reject) => {
    cognitoUser.setDeviceStatusRemembered({
      onSuccess: result => {
        setDeviceRememberTime();
        resolve(result);
      },
      onFailure: error => {
        console.error('Could not save device status remembered :>> ', error);
        resolve(error);
      },
    });
  });

/**
 * Sets the device status as not remembered
 * @param {object} cognitoUser The Cognito user
 * @returns {Promise} A promise that resolves when the device is not remembered
 */
export const setDeviceStatusNotRememberedSafe = cognitoUser =>
  new Promise((resolve, _reject) => {
    cognitoUser.setDeviceStatusNotRemembered({
      onSuccess: result => {
        resolve(result);
      },
      onFailure: error => {
        console.error(
          'Could not save device status not remembered :>> ',
          error,
        );

        resolve(error);
      },
    });
  });

/**
 * Checks if the device specifically throws the "not found" error, to address cognito bug with initial 2fa setup
 * @param {object} cognitoUser The Cognito user
 * @returns {Promise<boolean>} A promise that resolves with true if device is not found, false otherwise
 */
const isDeviceNotFound = cognitoUser =>
  new Promise((resolve, _reject) => {
    cognitoUser.getDevice({
      onSuccess: _result => {
        resolve(false);
      },
      onFailure: error => {
        resolve(error?.name === 'ResourceNotFoundException');
      },
    });
  });

/**
 * Lists all remembered devices for an authenticated user with pagination support
 * @param {object} cognitoUser The Cognito user
 * @returns {Promise<Array>} A promise that resolves with the complete list of device metadata
 */
const listAllDevices = cognitoUser =>
  new Promise((resolve, reject) => {
    const devices = [];

    const PAGE_SIZE = 20;

    const fetchDevices = (paginationToken = null) => {
      cognitoUser.listDevices(PAGE_SIZE, paginationToken, {
        onSuccess: result => {
          if (result.Devices && Array.isArray(result.Devices)) {
            devices.push(...result.Devices);
          }

          if (result.PaginationToken) {
            fetchDevices(result.PaginationToken);
          } else {
            resolve(devices);
          }
        },
        onFailure: error => {
          reject(error);
        },
      });
    };

    fetchDevices();
  });

export const setupMfaRememberDeviceSettings = async (cognitoUser, mfaType) => {
  if (mfaType === MFA_TYPES.DISABLED) {
    // Make sure the current device is not remembered when logging out
    await markAutoClearCache();

    const deviceListMeta = await listAllDevices(cognitoUser);

    for (let i = 0; i < deviceListMeta.length; i++) {
      const deviceKey = deviceListMeta[i]?.DeviceKey;

      // Forget all devices except the current one
      if (deviceKey && deviceKey !== cognitoUser.deviceKey) {
        cognitoUser.client.request(
          'ForgetDevice',
          {
            AccessToken: cognitoUser.signInUserSession
              .getAccessToken()
              .getJwtToken(),
            DeviceKey: deviceKey,
          },
          err => {
            if (err) {
              console.error('Error in ForgetDevice:', {
                deviceMetadata: deviceListMeta[i],
                error: err,
              });
            }
          },
        );
      }
    }
  } else {
    const notFound = await isDeviceNotFound(cognitoUser);

    if (notFound) {
      /**
       * This fixes a bug in cognito where the device is not remembered without calling set remembered
       * multiple times the first time 2fa is ever enabled for a particular user for the current session.
       */
      await setDeviceStatusRememberedSafe(cognitoUser);
    }
  }
};

// Exporting private functions for testing purposes
export const __test__ = {
  isDeviceNotFound,
  listAllDevices,
  clearCachedCognitoIdentityServiceProviderKeys,
};
